import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "error-container",
    role: "alert"
};
const _hoisted_2 = { class: "error-text" };
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuth } from "@/shared/composables/useAuth";
import { useLogger } from "@/shared/composables/useLogger";
import { keycloakAuthService } from "@/infrastructure/adapters/KeycloakAuthService";
import { keycloak } from "@/plugins/keycloak";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ConfirmOrganization',
    setup(__props) {
        const logger = useLogger();
        const router = useRouter();
        const route = useRoute();
        const { login } = useAuth();
        const organizationId = ref("");
        const isProcessing = ref(false);
        const error = ref(null);
        // Extract organization from route params and immediately redirect
        onMounted(async () => {
            const orgId = route.params.organizationId;
            if (!orgId) {
                logger.debug("No organization ID provided in route");
                error.value = "Organization identifier is missing.";
                return;
            }
            organizationId.value = orgId;
            logger.debug(`Confirm organization redirect page: Organization ID from route: ${orgId}`);
            // Wait for auth service to initialize
            await waitForAuthInitialization();
            // Check if user is already authenticated
            if (keycloakAuthService.isAuthenticated()) {
                const currentOrg = getCurrentOrganization();
                logger.debug(`User is authenticated. Current organization: ${currentOrg}, Target organization: ${orgId}`);
                // If user is already logged in with the same organization, redirect to dashboard
                if (currentOrg === orgId) {
                    logger.debug("User already logged in with the same organization, redirecting to dashboard");
                    router.push("/dashboard");
                    return;
                }
                // If user is logged in with a different organization, logout first
                logger.debug(`User logged in with different organization (${currentOrg} -> ${orgId}), logging out first`);
                try {
                    // Clear organization cache to allow new organization selection
                    localStorage.removeItem("organizationCache");
                    // Logout and then login with new organization
                    // Note: logout will redirect, so we need to handle this differently
                    // We'll logout locally and then immediately login with new org
                    await switchToNewOrganization(orgId);
                    return;
                }
                catch (err) {
                    logger.debug("Failed to switch organization:", err);
                    error.value = "Failed to switch organization. Please try again.";
                    return;
                }
            }
            // User is not authenticated, immediately trigger login with organization context
            // This will redirect to Keycloak with the organization in scope
            await initiateLoginWithOrganization(orgId);
        });
        /**
         * Wait for authentication service initialization
         */
        function waitForAuthInitialization() {
            return new Promise((resolve) => {
                let attempts = 0;
                const maxAttempts = 100; // 10 seconds max wait
                const checkInitialization = () => {
                    if (keycloakAuthService.isInitialized()) {
                        resolve();
                    }
                    else if (attempts < maxAttempts) {
                        attempts++;
                        setTimeout(checkInitialization, 100);
                    }
                    else {
                        logger.debug("Authentication service initialization timeout");
                        resolve(); // Resolve anyway to avoid blocking
                    }
                };
                checkInitialization();
            });
        }
        /**
         * Get current organization from token or cache
         */
        function getCurrentOrganization() {
            // Try to get from Keycloak token first
            const organizationObj = keycloak.idTokenParsed?.organization;
            if (organizationObj) {
                if (typeof organizationObj === "object" && organizationObj !== null) {
                    const keyOrganization = Object.keys(organizationObj)[0];
                    if (keyOrganization) {
                        logger.debug(`Current organization from token: ${keyOrganization}`);
                        return keyOrganization;
                    }
                }
                else if (typeof organizationObj === "string") {
                    logger.debug(`Current organization from token (string): ${organizationObj}`);
                    return organizationObj;
                }
            }
            // Fallback to localStorage cache
            const cachedOrg = localStorage.getItem("organizationCache");
            if (cachedOrg) {
                logger.debug(`Current organization from cache: ${cachedOrg}`);
                return cachedOrg;
            }
            logger.debug("No current organization found");
            return null;
        }
        /**
         * Switch to a new organization by logging out and logging in again
         */
        async function switchToNewOrganization(newOrgId) {
            if (isProcessing.value)
                return;
            isProcessing.value = true;
            error.value = null;
            try {
                logger.debug(`Switching organization to: ${newOrgId}`);
                // Clear organization cache
                localStorage.removeItem("organizationCache");
                // Clear any existing redirect path
                sessionStorage.removeItem("auth-redirect-path");
                // Set redirect path to dashboard after successful authentication
                // Add invite parameter to show success toast in dashboard
                const redirectPath = "/dashboard?invite=1";
                sessionStorage.setItem("auth-redirect-path", redirectPath);
                // Build redirect URI
                const redirectUri = window.location.origin + redirectPath;
                // Clear force-logout flag to allow reconnection
                sessionStorage.removeItem("force-logout");
                // Instead of full logout (which redirects), we'll clear tokens locally
                // and then immediately login with new organization
                // This provides a smoother transition
                // Clear tokens from sessionStorage
                sessionStorage.removeItem("kc-token");
                sessionStorage.removeItem("kc-refresh-token");
                // Clear Keycloak instance tokens
                if (keycloak.token) {
                    keycloak.token = undefined;
                }
                if (keycloak.refreshToken) {
                    keycloak.refreshToken = undefined;
                }
                if (keycloak.idToken) {
                    keycloak.idToken = undefined;
                }
                // Mark as not authenticated
                keycloak.authenticated = false;
                logger.debug(`Cleared current session, now logging in with new organization: ${newOrgId}`);
                logger.debug(`Redirect URI: ${redirectUri}`);
                // Immediately trigger login with new organization
                // This will redirect to Keycloak with the new organization in scope
                await login(redirectUri, undefined, newOrgId);
                // Note: login() will redirect to Keycloak, so code below won't execute
                logger.debug("Organization switch initiated successfully");
            }
            catch (err) {
                logger.debug("Failed to switch organization:", err);
                error.value = "Failed to switch organization. Please try again.";
                isProcessing.value = false;
            }
        }
        /**
         * Initiate login with organization context
         * This immediately redirects to Keycloak with organization in scope
         */
        async function initiateLoginWithOrganization(orgId) {
            if (isProcessing.value)
                return;
            isProcessing.value = true;
            error.value = null;
            try {
                logger.debug(`Redirecting to Keycloak with organization: ${orgId}`);
                // Clear any existing redirect path
                sessionStorage.removeItem("auth-redirect-path");
                // Set redirect path to dashboard after successful authentication
                // Add invite parameter to show success toast in dashboard
                const redirectPath = "/dashboard?invite=1";
                sessionStorage.setItem("auth-redirect-path", redirectPath);
                // Build redirect URI
                const redirectUri = window.location.origin + redirectPath;
                logger.debug(`Starting login with organization context: ${orgId}`);
                logger.debug(`Redirect URI: ${redirectUri}`);
                // Trigger login with organization parameter
                // This will pass the organization to Keycloak as idpHint and in scope
                // The login() call will redirect to Keycloak, so this page won't be visible
                await login(redirectUri, undefined, orgId);
                // Note: login() will redirect to Keycloak, so code below won't execute
                logger.debug("Login initiated successfully");
            }
            catch (err) {
                logger.debug("Failed to initiate login with organization:", err);
                error.value = "Failed to initiate login. Please try again.";
                isProcessing.value = false;
            }
        }
        return (_ctx, _cache) => {
            return (error.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                    _createElementVNode("p", _hoisted_2, _toDisplayString(error.value), 1 /* TEXT */)
                ]))
                : _createCommentVNode("v-if", true);
        };
    }
});
